import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import AddressForm from "../../../components/AddressForm";
import { useAuth } from "../../../providers/authprovider";
import RajcompanyLogo, { validateLogoFile } from "../../../components/RajcompanyLogo";

const EMPTY_COMPANY = {
  full_name: "",
  nip: "",
  regon: "",
  krs: "",
  phone: "",
  www: "",
  default: false,
  address: {
    street: "",
    building_num: "",
    local_num: "",
    postal_code: "",
    city: "",
    country: "Polska",
  },
};

// Pole pominiete w body zostawia stara wartosc, dopiero `null` czysci kolumne.
const emptyToNull = (value) => {
  const text = (value ?? "").toString().trim();

  return text === "" ? null : text;
};

export default function RajcompanyEditModal({ show, company, onHide, onSaved }) {
  const { rajcompanies } = useAuth();
  const [formData, setFormData] = useState(EMPTY_COMPANY);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [stampFile, setStampFile] = useState(null);
  const [stampPreview, setStampPreview] = useState(null);
  const [removeStamp, setRemoveStamp] = useState(false);
  const [fireCertificateFile, setFireCertificateFile] = useState(null);
  const [fireCertificatePreview, setFireCertificatePreview] = useState(null);
  const [removeFireCertificate, setRemoveFireCertificate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);
  const stampInputRef = useRef(null);
  const fireCertificateInputRef = useRef(null);
  const isEdit = !!company;
  const hasStoredLogo = !!company?.has_logo;
  const hasStoredStamp = !!company?.has_stamp;
  const hasStoredFireCertificate = !!company?.has_fire_certificate;

  useEffect(() => {
    if (!show) return;

    setFormData(
      company
        ? {
            ...company,
            nip: company.nip ?? "",
            regon: company.regon ?? "",
            krs: company.krs ?? "",
            phone: company.phone ?? "",
            www: company.www ?? "",
            default: !!company.default,
          }
        : EMPTY_COMPANY,
    );
    setLogoFile(null);
    setRemoveLogo(false);
    setStampFile(null);
    setRemoveStamp(false);
    setFireCertificateFile(null);
    setRemoveFireCertificate(false);
    setSaving(false);
    setError("");

    if (fileInputRef.current) fileInputRef.current.value = "";
    if (stampInputRef.current) stampInputRef.current.value = "";
    if (fireCertificateInputRef.current) fireCertificateInputRef.current.value = "";
  }, [show, company]);

  useEffect(() => {
    if (!logoFile) {
      setLogoPreview(null);
      return;
    }

    const url = URL.createObjectURL(logoFile);
    setLogoPreview(url);

    return () => URL.revokeObjectURL(url);
  }, [logoFile]);

  useEffect(() => {
    if (!stampFile) {
      setStampPreview(null);
      return;
    }

    const url = URL.createObjectURL(stampFile);
    setStampPreview(url);

    return () => URL.revokeObjectURL(url);
  }, [stampFile]);

  useEffect(() => {
    if (!fireCertificateFile) {
      setFireCertificatePreview(null);
      return;
    }

    const url = URL.createObjectURL(fireCertificateFile);
    setFireCertificatePreview(url);

    return () => URL.revokeObjectURL(url);
  }, [fireCertificateFile]);

  const handleChange = (event) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
  };

  const handleAddressChange = (data) => {
    setFormData({ ...formData, address: data.address });
  };

  const handleLogoChange = (event) => {
    const file = event.target.files?.[0] || null;
    const fileError = validateLogoFile(file);

    if (fileError) {
      setError(fileError);
      event.target.value = "";
      setLogoFile(null);
      return;
    }

    setError("");
    setLogoFile(file);
    setRemoveLogo(false);
  };

  const handleLogoRemove = () => {
    setLogoFile(null);
    setRemoveLogo(true);

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleStampChange = (event) => {
    const file = event.target.files?.[0] || null;
    const fileError = validateLogoFile(file);

    if (fileError) {
      setError(fileError);
      event.target.value = "";
      setStampFile(null);
      return;
    }

    setError("");
    setStampFile(file);
    setRemoveStamp(false);
  };

  const handleStampRemove = () => {
    setStampFile(null);
    setRemoveStamp(true);

    if (stampInputRef.current) stampInputRef.current.value = "";
  };

  const handleFireCertificateChange = (event) => {
    const file = event.target.files?.[0] || null;
    const fileError = validateLogoFile(file);

    if (fileError) {
      setError(fileError);
      event.target.value = "";
      setFireCertificateFile(null);
      return;
    }

    setError("");
    setFireCertificateFile(file);
    setRemoveFireCertificate(false);
  };

  const handleFireCertificateRemove = () => {
    setFireCertificateFile(null);
    setRemoveFireCertificate(true);

    if (fireCertificateInputRef.current) fireCertificateInputRef.current.value = "";
  };

  const handleClose = () => {
    if (saving) return;

    onHide();
  };

  const handleSave = () => {
    if (!formData.full_name.trim()) {
      setError("Wpisz pełną nazwę spółki.");
      return;
    }

    setSaving(true);
    setError("");

    const payload = {
      full_name: formData.full_name.trim(),
      nip: emptyToNull(formData.nip),
      regon: emptyToNull(formData.regon),
      krs: emptyToNull(formData.krs),
      phone: emptyToNull(formData.phone),
      www: emptyToNull(formData.www),
      default: !!formData.default,
      address: formData.address,
    };

    if (isEdit) payload.id = company.id;

    const finish = (savedCompany) => {
      setSaving(false);
      onSaved(savedCompany);
      onHide();
    };

    // Logo/pieczątka ida osobnym requestem multipart - ta droga ma walidacje
    // mimetype i rozmiaru po stronie serwera.
    const applyAsset = (companyData, { file, shouldRemove, hasStored, upload, removeAsset, label }, next) => {
      if (file) {
        upload(companyData.id, file, (result) => {
          if (!result.success) {
            setSaving(false);
            setError(`Zapisano spółkę, ale nie udało się wgrać ${label}. ` + result.error);
            onSaved(companyData);
            return;
          }

          next(result.content);
        });
        return;
      }

      if (shouldRemove && hasStored) {
        removeAsset(companyData.id, (result) => {
          if (!result.success) {
            setSaving(false);
            setError(`Zapisano spółkę, ale nie udało się usunąć ${label}. ` + result.error);
            onSaved(companyData);
            return;
          }

          next(result.content);
        });
        return;
      }

      next(companyData);
    };

    const callback = ({ success, error, content }) => {
      if (!success) {
        setSaving(false);
        setError(error);
        return;
      }

      applyAsset(
        content,
        {
          file: logoFile,
          shouldRemove: removeLogo,
          hasStored: hasStoredLogo,
          upload: rajcompanies.uploadLogo,
          removeAsset: rajcompanies.removeLogo,
          label: "logo",
        },
        (afterLogo) => {
          applyAsset(
            afterLogo,
            {
              file: stampFile,
              shouldRemove: removeStamp,
              hasStored: hasStoredStamp,
              upload: rajcompanies.uploadStamp,
              removeAsset: rajcompanies.removeStamp,
              label: "pieczątkę",
            },
            (afterStamp) => {
              applyAsset(
                afterStamp,
                {
                  file: fireCertificateFile,
                  shouldRemove: removeFireCertificate,
                  hasStored: hasStoredFireCertificate,
                  upload: rajcompanies.uploadFireCertificate,
                  removeAsset: rajcompanies.removeFireCertificate,
                  label: "świadectwo przeciwpożarowe",
                },
                finish,
              );
            },
          );
        },
      );
    };

    if (isEdit) {
      rajcompanies.edit(payload, callback);
      return;
    }

    rajcompanies.add(payload, callback);
  };

  const showStoredLogo = isEdit && hasStoredLogo && !removeLogo && !logoFile;
  const canRemoveLogo = !!logoFile || (hasStoredLogo && !removeLogo);
  const showStoredStamp = isEdit && hasStoredStamp && !removeStamp && !stampFile;
  const canRemoveStamp = !!stampFile || (hasStoredStamp && !removeStamp);
  const showStoredFireCertificate =
    isEdit && hasStoredFireCertificate && !removeFireCertificate && !fireCertificateFile;
  const canRemoveFireCertificate =
    !!fireCertificateFile || (hasStoredFireCertificate && !removeFireCertificate);

  return (
    <Modal show={show} onHide={handleClose} centered size="lg" backdrop="static">
      <Modal.Header closeButton={!saving}>
        <Modal.Title>{isEdit ? "Edytuj spółkę" : "Dodaj spółkę"}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        <Form>
          <Form.Group className="mb-3" controlId="rajcompany_full_name">
            <Form.Label>Pełna nazwa spółki</Form.Label>
            <Form.Control
              value={formData.full_name}
              type="text"
              name="full_name"
              onChange={handleChange}
              disabled={saving}
            />
          </Form.Group>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="rajcompany_nip">
                <Form.Label>NIP</Form.Label>
                <Form.Control
                  value={formData.nip}
                  type="text"
                  name="nip"
                  onChange={handleChange}
                  disabled={saving}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="rajcompany_regon">
                <Form.Label>REGON</Form.Label>
                <Form.Control
                  value={formData.regon}
                  type="text"
                  name="regon"
                  onChange={handleChange}
                  disabled={saving}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3" controlId="rajcompany_krs">
            <Form.Label>KRS</Form.Label>
            <Form.Control
              value={formData.krs}
              as="textarea"
              rows={2}
              maxLength={1024}
              name="krs"
              onChange={handleChange}
              disabled={saving}
            />
          </Form.Group>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="rajcompany_phone">
                <Form.Label>Telefon</Form.Label>
                <Form.Control
                  value={formData.phone}
                  type="tel"
                  name="phone"
                  onChange={handleChange}
                  disabled={saving}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="rajcompany_www">
                <Form.Label>WWW</Form.Label>
                <Form.Control
                  value={formData.www}
                  type="text"
                  name="www"
                  placeholder="np. www.firma.pl"
                  onChange={handleChange}
                  disabled={saving}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3" controlId="rajcompany_default">
            <Form.Check
              type="checkbox"
              label="Spółka domyślna"
              checked={!!formData.default}
              disabled={saving}
              onChange={(event) =>
                setFormData({ ...formData, default: event.target.checked })
              }
            />
          </Form.Group>

          <AddressForm onChange={handleAddressChange} default={formData.address} />

          <Form.Group className="mb-3" controlId="rajcompany_logo">
            <Form.Label>Logo spółki</Form.Label>
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div
                className="border rounded bg-white d-flex align-items-center justify-content-center"
                style={{ width: 140, height: 80, overflow: "hidden" }}
              >
                {logoPreview && (
                  <img
                    src={logoPreview}
                    alt="Podgląd logo"
                    style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                  />
                )}
                {!logoPreview && showStoredLogo && (
                  <RajcompanyLogo companyId={company.id} hasLogo height={70} />
                )}
                {!logoPreview && !showStoredLogo && (
                  <span className="text-muted small">Brak logo</span>
                )}
              </div>

              <div className="flex-grow-1" style={{ minWidth: 240 }}>
                <Form.Control
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  disabled={saving}
                />
                <div className="text-muted small mt-1">
                  Plik graficzny, maksymalnie 5 MB.
                </div>
                {canRemoveLogo && (
                  <Button
                    size="sm"
                    variant="outline-danger"
                    className="mt-2"
                    onClick={handleLogoRemove}
                    disabled={saving}
                  >
                    Usuń logo
                  </Button>
                )}
                {removeLogo && hasStoredLogo && (
                  <div className="text-danger small mt-2">
                    Logo zostanie usunięte po zapisaniu.
                  </div>
                )}
              </div>
            </div>
          </Form.Group>

          <Form.Group className="mb-3" controlId="rajcompany_stamp">
            <Form.Label>Pieczątka spółki</Form.Label>
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div
                className="border rounded bg-white d-flex align-items-center justify-content-center"
                style={{ width: 140, height: 80, overflow: "hidden" }}
              >
                {stampPreview && (
                  <img
                    src={stampPreview}
                    alt="Podgląd pieczątki"
                    style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                  />
                )}
                {!stampPreview && showStoredStamp && (
                  <RajcompanyLogo companyId={company.id} hasLogo type="stamp" height={70} />
                )}
                {!stampPreview && !showStoredStamp && (
                  <span className="text-muted small">Brak pieczątki</span>
                )}
              </div>

              <div className="flex-grow-1" style={{ minWidth: 240 }}>
                <Form.Control
                  ref={stampInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleStampChange}
                  disabled={saving}
                />
                <div className="text-muted small mt-1">
                  Plik graficzny, maksymalnie 5 MB.
                </div>
                {canRemoveStamp && (
                  <Button
                    size="sm"
                    variant="outline-danger"
                    className="mt-2"
                    onClick={handleStampRemove}
                    disabled={saving}
                  >
                    Usuń pieczątkę
                  </Button>
                )}
                {removeStamp && hasStoredStamp && (
                  <div className="text-danger small mt-2">
                    Pieczątka zostanie usunięta po zapisaniu.
                  </div>
                )}
              </div>
            </div>
          </Form.Group>

          <Form.Group className="mb-3" controlId="rajcompany_fire_certificate">
            <Form.Label>Świadectwo przeciwpożarowe spółki</Form.Label>
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div
                className="border rounded bg-white d-flex align-items-center justify-content-center"
                style={{ width: 140, height: 80, overflow: "hidden" }}
              >
                {fireCertificatePreview && (
                  <img
                    src={fireCertificatePreview}
                    alt="Podgląd świadectwa przeciwpożarowego"
                    style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                  />
                )}
                {!fireCertificatePreview && showStoredFireCertificate && (
                  <RajcompanyLogo
                    companyId={company.id}
                    hasLogo
                    type="fire_certificate"
                    height={70}
                  />
                )}
                {!fireCertificatePreview && !showStoredFireCertificate && (
                  <span className="text-muted small">Brak świadectwa</span>
                )}
              </div>

              <div className="flex-grow-1" style={{ minWidth: 240 }}>
                <Form.Control
                  ref={fireCertificateInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFireCertificateChange}
                  disabled={saving}
                />
                <div className="text-muted small mt-1">
                  Plik graficzny, maksymalnie 5 MB.
                </div>
                {canRemoveFireCertificate && (
                  <Button
                    size="sm"
                    variant="outline-danger"
                    className="mt-2"
                    onClick={handleFireCertificateRemove}
                    disabled={saving}
                  >
                    Usuń świadectwo
                  </Button>
                )}
                {removeFireCertificate && hasStoredFireCertificate && (
                  <div className="text-danger small mt-2">
                    Świadectwo zostanie usunięte po zapisaniu.
                  </div>
                )}
              </div>
            </div>
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={saving}>
          Anuluj
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving && <Spinner size="sm" className="me-2" />}
          Zapisz
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
